import { isUndefined } from 'lodash';
import { DateTime } from 'luxon';
import classNames from 'classnames';

import { ClientSummaryNode } from '../../../../generated/webapp_gql';
import { CurrentUser } from '../../../../rematch-models/user';
import { ProviderAvailability } from './constants';
import { InsuranceStatus } from '../../../../constants';

export const isEligibleForHpiSessions = (selectedClient: ClientSummaryNode) => {
  if (selectedClient.visitsRemaining > 0) return true;

  return (
    selectedClient.isCompanyHpiEnabled &&
    selectedClient.memberInsuranceStatus !== InsuranceStatus.NOT_ELIGIBLE
  );
};

export const canScheduleClient = ({
  currentUser,
  selectedClient,
  selectedDatetime,
  showHpiClientStatusInfo,
}: {
  currentUser: CurrentUser;
  selectedClient: ClientSummaryNode | undefined;
  selectedDatetime: Date | undefined;
  showHpiClientStatusInfo: boolean;
}) => {
  if (isUndefined(selectedClient) || isUndefined(selectedDatetime)) return false;

  return (
    !showHpiClientStatusInfo || !currentUser.isTherapist || isEligibleForHpiSessions(selectedClient)
  );
};

const getSessionContextFromCurrentUser = (currentUser: CurrentUser) =>
  currentUser.isTherapist ? 'therapy' : 'coaching';

const buildErrorResponseForNoRemainingVisits = ({
  currentUser,
  selectedClient,
}: {
  currentUser: CurrentUser;
  selectedClient: ClientSummaryNode;
}) => {
  const memberName = selectedClient
    ? `${selectedClient.memberFirstName} ${selectedClient.memberLastName}`
    : 'your client';

  return `It looks like ${memberName} doesn't have any remaining ${getSessionContextFromCurrentUser(
    currentUser,
  )} sessions.`;
};

const ERROR_RESPONSE_MESSAGES = {
  defaultMessage: 'Something went wrong! Please try again later.',
  noActiveMatchWithProvider: "Oops, it looks like this client isn't matched with you.",
  overlappingVisit: 'Oops, it looks like that session is no longer available 😞',
  reachedMaxVisits: 'This client is already booked for the maximum number of visits on this day.',
};

const hasOverlappingVisit = (error: string) =>
  error.match(/has existing overlapping visit with UUID/);
const hasReachedMaxVisits = (error: string) => error.match(/already has the maximum visits/);
const hasNoRemainingVisits = (error: string) => error.match(/Member has no remaining .* visits/);
const hasNoActiveMatchWithProvider = (error: string) =>
  error.match(/does not have an active match with the specified provider/);
const hasBookedLessTenMinutesBeforeStart = (error: string) =>
  error.match(/cannot be booked less than 10 minutes before/);

export const getDisplayErrorMessage = ({
  currentUser,
  error,
  selectedClient,
}: {
  currentUser: CurrentUser;
  error: string;
  selectedClient: ClientSummaryNode;
}) => {
  if (hasOverlappingVisit(error)) {
    return ERROR_RESPONSE_MESSAGES.overlappingVisit;
  }

  if (hasReachedMaxVisits(error)) {
    return ERROR_RESPONSE_MESSAGES.reachedMaxVisits;
  }

  if (hasNoRemainingVisits(error)) {
    return buildErrorResponseForNoRemainingVisits({ currentUser, selectedClient });
  }

  if (hasNoActiveMatchWithProvider(error)) {
    return ERROR_RESPONSE_MESSAGES.noActiveMatchWithProvider;
  }

  if (hasBookedLessTenMinutesBeforeStart(error)) {
    return error;
  }

  return ERROR_RESPONSE_MESSAGES.defaultMessage;
};

// Helper to convert date string (no timezone set) from providerAvailability to date object to local time
export const convertStrToDateWithLocalTimezone = (dateString: string): Date => {
  return new Date(`${dateString}Z`);
};

export const getScheduleButtonClassName = ({ isFormValid }) =>
  classNames(
    'disabled:bg-transparent',
    'disabled:text-gray-400',
    'disabled:border-gray-400',
    'disabled:shadow-none',
    'hover:bg-blue-300',
    'hover:shadow-lg',
    'bg-blue-200',
    'text-white',
    'font-bold',
    'py-2',
    'px-12',
    'ml-3',
    'border',
    'border-blue-200',
    'rounded',
    'text-base',
    {
      'cursor-not-allowed': !isFormValid,
    },
  );

export const getSessionDurationInMinutes = (
  providerAvailability: ProviderAvailability | undefined,
) => {
  if (isUndefined(providerAvailability)) return;

  const start = DateTime.fromISO(providerAvailability.start);
  const end = DateTime.fromISO(providerAvailability.end);

  const diff = end.diff(start, 'minutes').toObject();

  return diff?.minutes;
};
